'use client';
import React, { useLayoutEffect, useRef, useState } from 'react';

const STEPS = [
  'Product Designer II',
  'UX/UI Design Specialist',
  'Digital Experience Designer',
  'Interface Design Lead',
];

const RESULT = 'Web and Digital Interface Designers';

type Line = { x1: number; y1: number; x2: number; y2: number };

const AboutCategoriesDC = () => {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const resultRef = useRef<HTMLDivElement | null>(null);
  const [lines, setLines] = useState<Line[]>([]);

  useLayoutEffect(() => {
    function measure() {
      const wrap = wrapRef.current;
      const result = resultRef.current;
      if (!wrap || !result) return;

      if (window.innerWidth < 768) {
        setLines([]);
        return;
      }

      const wrapRect = wrap.getBoundingClientRect();
      const resultRect = result.getBoundingClientRect();

      const rows = rowRefs.current.filter((el): el is HTMLDivElement => !!el);
      if (rows.length === 0) return;

      const rowPoints = rows.map((el) => {
        const r = el.getBoundingClientRect();
        return {
          x: r.right - wrapRect.left,
          y: r.top + r.height / 2 - wrapRect.top,
        };
      });

      const spineX = rowPoints[0].x + 14;

      const resultY = resultRect.top + resultRect.height / 2 - wrapRect.top;
      const resultX = resultRect.left - wrapRect.left;

      const newLines: Line[] = [
        ...rowPoints.map((p) => ({ x1: p.x, y1: p.y, x2: spineX, y2: p.y })),
        {
          x1: spineX,
          y1: rowPoints[0].y,
          x2: spineX,
          y2: rowPoints[rowPoints.length - 1].y,
        },
        { x1: spineX, y1: resultY, x2: resultX, y2: resultY },
      ];

      setLines(newLines);
    }

    measure();

    const ro = new ResizeObserver(measure);
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener('resize', measure);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  return (
    <div className="w-full py-20 bg-white border-t border-dc-gallery">
      <div className="px-4 container items-center justify-center">
        <p className="text-dc-rock-spray font-bold text-sm">CATEGORIES, NOT TITLES</p>
        <h2 className="mt-5 text-dc-cod-gray font-bold max-md:text-3xl md:text-[40px] md:leading-11">
          Why we don&apos;t match on job titles
        </h2>
        <p className="mt-5 text-dc-cod-gray max-w-175">
          Companies invent titles. Standard occupation categories don&apos;t move, so a ranking you
          set once keeps working even as the market renames everything.
        </p>
        <div className="w-full px-4 py-4 md:px-0">
          <div
            ref={wrapRef}
            className="relative flex flex-col md:flex-row md:items-center md:gap-8"
          >
            <div className="relative w-full md:w-125">
              <div className="flex flex-col gap-3">
                {STEPS.map((occupation, i) => (
                  <div
                    key={occupation}
                    ref={(el) => {
                      rowRefs.current[i] = el;
                    }}
                    className="
                  flex h-14 items-center
                  rounded-xl
                  bg-dc-wild-sand
                  px-4
                  text-dc-cod-gray
                "
                  >
                    {occupation}
                  </div>
                ))}
              </div>
            </div>

            <div className="flex h-8 justify-center md:hidden">
              <div className="w-px bg-dc-gallery" />
            </div>

            <div
              ref={resultRef}
              className="w-full min-h-14 rounded-xl bg-dc-vermilion12 px-4 py-3 flex items-center font-bold text-dc-rock-spray md:w-125"
            >
              {RESULT}
            </div>

            {lines.length > 0 && (
              <svg
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 hidden h-full w-full md:block"
                style={{ overflow: 'visible' }}
              >
                {lines.map((l, idx) => (
                  <line key={idx} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} stroke="#e6e6e6" />
                ))}
              </svg>
            )}
          </div>
        </div>
        <p className="mt-6 text-dc-dove-gray max-w-175 text-sm">
          Every incoming role is mapped to a standard occupation category before it reaches anyone.
          You rank the category once; the mapping absorbs whatever a company decides to call it.
        </p>
      </div>
    </div>
  );
};

export default AboutCategoriesDC;
